// RD-163: локальный индекс «Найти настройку» — названия разделов, блоков, параметров,
// подписи и ключи существующих прав. Значения полей и данные людей в индекс не входят.

import {
    EXCHANGE_DELAY_FIELDS,
    EXCHANGE_RULE_FIELDS,
    ROLE_DAYS_KEY,
    ROLE_PERMISSIONS,
    STATISTIC_FIELDS,
    TIME_FIELDS,
    type SettingsSectionId,
} from './settingsModel'

export interface SettingsNavTarget {
    section: SettingsSectionId
    roleId?: number | null
    anchor?: string
    groupId?: string
}

export interface SettingsSearchEntry {
    id: string
    label: string
    context: string
    techKey?: string
    target: SettingsNavTarget
    /** Для права — роли, в которых его можно открыть. */
    roles?: Array<{ id: number, name: string }>
    haystack: string
}

export const SECTION_LABELS: Record<SettingsSectionId, string> = {
    general: 'Общие настройки',
    roles: 'Роли и права',
    positions: 'Должности',
    exchange: 'Биржа смен',
}

export const SECTION_BLOCKS: Record<SettingsSectionId, Array<{ anchor: string, label: string }>> = {
    general: [
        { anchor: 'block-time', label: 'Учёт времени' },
        { anchor: 'block-statistics', label: 'Показатели сотрудника' },
        { anchor: 'block-lunch', label: 'Обеденные перерывы' },
    ],
    roles: [],
    positions: [],
    exchange: [
        { anchor: 'block-availability', label: 'Доступность вакансии' },
        { anchor: 'block-rules', label: 'Правила подбора' },
    ],
}

const entry = (value: Omit<SettingsSearchEntry, 'haystack'>, extra: string[] = []): SettingsSearchEntry => ({
    ...value,
    haystack: [value.label, value.techKey ?? '', ...extra].join(' ').toLocaleLowerCase('ru'),
})

export function buildSettingsSearchIndex(
    availableSections: SettingsSectionId[],
    roles: Array<{ id: number, name: string }>,
): SettingsSearchEntry[] {
    const index: SettingsSearchEntry[] = []
    const has = (section: SettingsSectionId) => availableSections.includes(section)

    availableSections.forEach((section) => {
        index.push(entry({ id: `section:${section}`, label: SECTION_LABELS[section], context: 'Раздел', target: { section } }))
        SECTION_BLOCKS[section].forEach((block) => {
            index.push(entry({ id: `block:${block.anchor}`, label: block.label, context: SECTION_LABELS[section], target: { section, anchor: block.anchor } }))
        })
    })

    if (has('general')) {
        TIME_FIELDS.forEach((field) => {
            index.push(entry({ id: `param:${field.key}`, label: field.label, context: 'Общие настройки · Учёт времени', target: { section: 'general', anchor: `setting-${field.key}` } }))
        })
        STATISTIC_FIELDS.forEach((field) => {
            index.push(entry(
                { id: `param:${field.key}`, label: `${field.code} — ${field.label}`, context: 'Общие настройки · Показатели сотрудника', target: { section: 'general', anchor: `setting-${field.key}` } },
            ))
        })
    }

    if (has('exchange')) {
        EXCHANGE_DELAY_FIELDS.forEach((field) => {
            index.push(entry({ id: `param:${field.key}`, label: field.label, context: 'Биржа смен · Доступность вакансии', target: { section: 'exchange', anchor: `setting-${field.key}` } }))
        })
        EXCHANGE_RULE_FIELDS.forEach((field) => {
            index.push(entry({ id: `param:${field.key}`, label: field.label, context: 'Биржа смен · Правила подбора', target: { section: 'exchange', anchor: `setting-${field.key}` } }))
        })
    }

    if (has('roles')) {
        const roleRefs = roles.map((role) => ({ id: role.id, name: role.name }))
        ROLE_PERMISSIONS.forEach((permission) => {
            index.push(entry({
                id: `permission:${permission.key}`,
                label: permission.label,
                techKey: permission.key,
                context: `Роли и права · ${permission.groupLabel}`,
                target: { section: 'roles', anchor: `permission-${permission.key}`, groupId: permission.groupId },
                roles: roleRefs,
            }))
        })
        index.push(entry({
            id: `permission:${ROLE_DAYS_KEY}`,
            label: 'Дней в прошлом для редактирования факта',
            techKey: ROLE_DAYS_KEY,
            context: 'Роли и права · Фактическое время и контроль',
            target: { section: 'roles', anchor: `permission-${ROLE_DAYS_KEY}`, groupId: 'fact' },
            roles: roleRefs,
        }))
    }

    return index
}

export function searchSettings(index: SettingsSearchEntry[], query: string, limit = 40): SettingsSearchEntry[] {
    const normalized = query.trim().toLocaleLowerCase('ru')
    if (!normalized) return []
    return index.filter((item) => item.haystack.includes(normalized)).slice(0, limit)
}
